import { Injectable, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export interface ConfirmDialogOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonType?: 'primary' | 'danger' | 'warning';
  /** true ise footer iptal butonu gizlenir; yalnızca onay gösterilir */
  hideCancel?: boolean;
  /** true ise sağ üst X kapat butonu gösterilir (hideCancel ile birlikte kullanılabilir) */
  showCloseButton?: boolean;
}

export interface ConfirmDialogData extends ConfirmDialogOptions {
  id: string;
}

@Injectable({
  providedIn: 'root',
})
export class ConfirmDialogService {
  private readonly _dialogs = signal<ConfirmDialogData[]>([]);
  readonly dialogs = this._dialogs.asReadonly();

  /**
   * Shows a confirm dialog and returns an Observable that emits true if confirmed, false if cancelled
   */
  confirm(options: ConfirmDialogOptions): Observable<boolean> {
    const dialogId = this.generateId();
    const subject = new Subject<boolean>();

    const dialogData: ConfirmDialogData = {
      id: dialogId,
      title: options.title,
      message: options.message,
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      confirmButtonType: options.confirmButtonType || 'primary',
      hideCancel: options.hideCancel ?? false,
      showCloseButton: options.showCloseButton ?? false,
    };

    this._dialogs.update((dialogs) => [...dialogs, dialogData]);

    // Store the subject for this dialog
    (dialogData as any)._subject = subject;

    return subject.asObservable();
  }

  /**
   * Confirms a dialog by id
   */
  confirmDialog(id: string): void {
    const dialog = this._dialogs().find((d) => d.id === id);
    if (dialog) {
      const subject = (dialog as any)._subject as Subject<boolean>;
      if (subject) {
        subject.next(true);
        subject.complete();
      }
      this.remove(id);
    }
  }

  /**
   * Cancels a dialog by id
   */
  cancelDialog(id: string): void {
    const dialog = this._dialogs().find((d) => d.id === id);
    if (dialog) {
      const subject = (dialog as any)._subject as Subject<boolean>;
      if (subject) {
        subject.next(false);
        subject.complete();
      }
      this.remove(id);
    }
  }

  /**
   * Removes a dialog by id
   */
  remove(id: string): void {
    this._dialogs.update((dialogs) => dialogs.filter((d) => d.id !== id));
  }

  /**
   * Clears all dialogs
   */
  clear(): void {
    this._dialogs().forEach((dialog) => {
      const subject = (dialog as any)._subject as Subject<boolean>;
      if (subject) {
        subject.next(false);
        subject.complete();
      }
    });
    this._dialogs.set([]);
  }

  private generateId(): string {
    return `confirm-dialog-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}





















